"use client";
import { useEffect } from "react";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import { useTranslation } from "@/hooks/translationContext";
import { useHeader } from "@/hooks/headerContext";
import { useNavigation } from "@/hooks/useNavigation";
import { Button } from "@/components/ui/button";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import Squares2X2Icon from "@heroicons/react/24/outline/Squares2X2Icon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";

const emptySelection = { id: 0, title: "", slug: "" };

export default function HeroSearchDesktop() {
  const { setIsOpen, setCurrentTab, setInitialFocus } = useSearchContext();
  const { t } = useTranslation();
  const { district, service, onChange } = useHeader();

  const hasDistrict = !!district?.id;
  const hasService = !!service?.id;

  const districtLabel = t("header-quicksearch-district", {
    defaultValue: "Semt Seçin",
  });

  const serviceLabel = t("header-quicksearch-service", {
    defaultValue: "Hizmet Seçin",
  });

  const searchLabel = t("text-search", { defaultValue: "Ara" });

  const { handleNavigation } = useNavigation({
    selectedDistrict: hasDistrict ? district : emptySelection,
    selectedService: hasService ? service : emptySelection,
    onChangeSelectedData: onChange,
  });

  // A carried-over selection is always a district/service search, so keep the
  // shared tab on 0 in case the panel was last left on another tab.
  useEffect(() => {
    if (hasDistrict || hasService) setCurrentTab(0);
  }, [hasDistrict, hasService, setCurrentTab]);

  const openSearch = (focus: "district" | "service" | null) => {
    setCurrentTab(0);
    setInitialFocus(focus);
    setIsOpen(true);
  };

  // With a district/service already carried into the boxes, run the search
  // directly; otherwise open the panel so the user can pick one.
  const onSearch = () => {
    if (hasDistrict || hasService) {
      handleNavigation();
    } else {
      openSearch(null);
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-[clamp(0.5rem,1vw,0.75rem)] bg-white rounded-2xl p-[clamp(0.875rem,1.35vw,1.125rem)] shadow-xl">
        <SelectTrigger
          icon={MapPinIcon}
          label={hasDistrict ? district.title : districtLabel}
          selected={hasDistrict}
          onClick={() => openSearch("district")}
        />

        <SelectTrigger
          icon={Squares2X2Icon}
          label={hasService ? service.title : serviceLabel}
          selected={hasService}
          onClick={() => openSearch("service")}
        />

        <Button
          onClick={onSearch}
          variant="primary-dark"
          className="!h-[clamp(56px,4.5vw,68px)] w-[clamp(7rem,10vw,12rem)] rounded-xl font-bold text-[clamp(0.875rem,0.9vw,1rem)] gap-2 shrink-0"
        >
          <MagnifyingGlassIcon className="size-5" />
          {searchLabel}
        </Button>
      </div>
    </div>
  );
}

function SelectTrigger({
  icon: Icon,
  label,
  selected,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 flex items-center justify-between gap-2 px-[clamp(0.75rem,1.2vw,1rem)] h-[clamp(56px,4.5vw,68px)] rounded-xl border border-gray-200 text-left hover:bg-gray-secondary transition-colors"
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon className="size-5 text-primary shrink-0" />
        <span
          className={`text-[clamp(0.875rem,0.9vw,1rem)] text-primary truncate ${selected ? "font-bold" : "font-semibold"}`}
        >
          {label}
        </span>
      </span>
      <ChevronDownIcon className="size-5 text-primary shrink-0" />
    </button>
  );
}
